import { useCallback, useEffect, useRef, useState } from "react";
import "./DataTable.css";

/**
 * Reusable, horizontally-scrollable table.
 *
 * columns: [{
 *   key,                 // unique key
 *   header,              // header label
 *   width,               // CSS grid track, e.g. "1fr" | "minmax(220px, 1fr)" | "120px"  (default "1fr")
 *   align,               // "start" | "end"
 *   sticky,              // true → pinned to the right edge while scrolling (use for actions)
 *   render(row),         // cell content
 *   skeleton,            // optional node shown while loading
 * }]
 */
const cellClass = (col) =>
  [
    "dt__cell",
    col.align === "end" && "dt__cell--end",
    col.sticky && "dt__cell--sticky",
  ]
    .filter(Boolean)
    .join(" ");

const DataTable = ({
  columns,
  rows = [],
  loading = false,
  skeletonRows = 10,
  getRowKey = (row) => row.id,
  emptyMessage = "No results found",
  minWidth = 720,
  footer = null,
}) => {
  const template = columns.map((c) => c.width || "1fr").join(" ");

  // True while content is scrolled underneath the pinned column (drives its edge shadow)
  const scrollRef = useRef(null);
  const [overflowing, setOverflowing] = useState(false);

  const updateOverflow = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setOverflowing(el.scrollWidth - el.clientWidth - el.scrollLeft > 1);
  }, []);

  useEffect(() => {
    updateOverflow();
    const el = scrollRef.current;
    if (!el) return undefined;

    const observer =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(updateOverflow)
        : null;
    observer?.observe(el);
    if (el.firstElementChild) observer?.observe(el.firstElementChild);
    window.addEventListener("resize", updateOverflow);

    return () => {
      observer?.disconnect();
      window.removeEventListener("resize", updateOverflow);
    };
  }, [updateOverflow, minWidth, template, rows.length, loading]);

  return (
    <div className={`dt${overflowing ? " dt--overflow" : ""}`}>
      <div className="dt__scroll" ref={scrollRef} onScroll={updateOverflow}>
        <div
          className="dt__table"
          role="table"
          style={{ "--dt-cols": template, "--dt-min": `${minWidth}px` }}
        >
          <div className="dt__row dt__row--head" role="row">
            {columns.map((col) => (
              <div key={col.key} role="columnheader" className={cellClass(col)}>
                {col.header}
              </div>
            ))}
          </div>

          <div role="rowgroup">
            {loading
              ? Array.from({ length: skeletonRows }).map((_, i) => (
                  <div className="dt__row" role="row" key={i}>
                    {columns.map((col) => (
                      <div key={col.key} role="cell" className={cellClass(col)}>
                        {col.skeleton ?? (
                          <div className="skeleton dt__skeleton" />
                        )}
                      </div>
                    ))}
                  </div>
                ))
              : rows.map((row) => (
                  <div className="dt__row" role="row" key={getRowKey(row)}>
                    {columns.map((col) => (
                      <div key={col.key} role="cell" className={cellClass(col)}>
                        {col.render(row)}
                      </div>
                    ))}
                  </div>
                ))}
          </div>
        </div>
      </div>

      {!loading && rows.length === 0 && (
        <div className="dt__empty">{emptyMessage}</div>
      )}

      {footer}
    </div>
  );
};

export default DataTable;
